import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { gql } from 'apollo-angular';
import { Observable, Subject, Subscription, firstValueFrom, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { DeleteAccountCheck, DeleteAccountEligibilityRes } from 'src/app/shared/models/delete-account.model';
import { ConfirmAlertButton, PromptsService } from 'src/app/shared/services/prompts.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { isInvalidUserError } from 'src/app/shared/types/inactive-user-error.type';
import { v4 as uuid_v4 } from 'uuid';
import { User } from '../models/gql/user.model';
import { AuthUser } from '../models/user.model';
import { ApiInterfaceService } from './api-interface.service';
import { AuthTokenService } from './auth-token.service';
import { BaseService } from './base.service';
import { StorageService } from './storage.service';
/**
 * The feature flag uid of the user while they are not logged in.
 */
const LOCAL_STORAGE_USER_UUID = 'local_storage_user_uuid';

/*
  Provides CRUD interface for user account.
*/

@Injectable({
  providedIn: 'root',
})
export class AccountService extends BaseService {
  /** Graphql class member */
  user: User;

  private currentUser: AuthUser;

  // User account subject used for sharing the user changes communicated through action cable.
  userSubj: Subject<User> = new Subject<User>();

  // The user being impersonated
  private targetUser: AuthUser;

  // Key to save target uuid in local storage
  private readonly TARGET_UUID_KEY = 'target-uuid';

  // Account channel subscription
  accountChannelSub: Subscription;

  // Account broadcaster subscription
  accountSubscription: Subscription;

  constructor(
    private api: ApiInterfaceService,
    private storage: StorageService,
    private authTokenService: AuthTokenService,
    private utilityService: UtilityService,
    private router: Router,
    private promptsService: PromptsService,
    private platform: Platform,
  ) {
    super({ resource: 'settings' });
    this.fetchAccountOnAppResume();
  }

  async fetchAccountOnAppResume(): Promise<void> {
    await this.platform.ready();

    this.platform.resume.pipe(takeUntil(this.unsubscribe)).subscribe(async () => {
      await this.account(true);
    });
  }

  public setUserSubj(user): void {
    this.userSubj.next(user);
  }

  /**
   * Beware that this observable will emit null when user logs out, so
   * you should be checking the value emitted from this observable
   * prior to referencing / deconstructing its properties.
   */
  get user$(): Observable<User> {
    return this.userSubj.asObservable();
  }

  /**
   * Update the users account
   *
   * @param user - user data
   */
  async update(user: User): Promise<User> {
    this.retrieveStarted();
    try {
      const result = await this.api.gqlMutation(
        gql`
          mutation carerAccountUpdate($UserAccountUpdateAttributes: UserAccountUpdateAttributes!) {
            userAccountUpdate(input: { userAttributes: $UserAccountUpdateAttributes }) {
              user {
                id
              }
            }
          }
        `,
        { UserAccountUpdateAttributes: user },
      );
      this.retrieveStopped();
      // TODO (GE-553): Accounts graphQL subscription, this result is not the user object. This needs to be fixed.
      this.account(true); // This hack must be removed from here
      return result;
    } catch (e) {
      this.retrieveStopped();
      return Promise.reject(e);
    }
  }

  /**
   * Gets user UUID (captain obvious).
   */
  public get uuid(): string {
    /**
     * Return token UUID if user is logged in.
     * Else reading key from local Storage.
     * .     E.g. For signup, the split test on ember signup will create a user uid and store it in the local storage.
     * .     We need to pass the same id to backend and feature flag.
     * Else return a v4 generated UID.
     * .     We will be generating UID for tracking pages with no auth (e.g. Register/login).
     *
     * Keeping 'optimizely_user_id' for backwards compatibility
     */
    const userId =
      this.user?.uuid || localStorage.getItem(LOCAL_STORAGE_USER_UUID) || localStorage.getItem('optimizely_user_id');

    // TODO: Discuss the case when a single user is signing up on behalf of multiple users using the same system
    if (!userId) {
      // Generating user ID with v4 - Used on pages with no auth (register/login)
      const id = uuid_v4();

      try {
        // Save in local variable to avoid creating multiple keys for the same user.
        localStorage.setItem(LOCAL_STORAGE_USER_UUID, id);
      } catch {
        return id;
      }
      return id;
    }
    return userId;
  }

  async updateCurrentAndTargetUser(): Promise<void> {
    try {
      await Promise.all([this.fetchCurrentUser(), this.fetchTargetUser()]);
    } catch (error: unknown) {
      // If user is not active display the permission denied prompt and logout the user.
      if (isInvalidUserError(error) && error.error.error === 'User not active') {
        const confirmAlertActions: [ConfirmAlertButton] = [
          {
            text: 'OK',
            handler: () => {
              this.router.navigate(['/logout']);
            },
          },
        ];
        this.promptsService.showConfirmAlert({
          header: 'Permission denied',
          message: 'Please make sure you have valid permission to use the app.',
          buttons: confirmAlertActions,
        });
      } else {
        this.utilityService.showErrorAlert('Failed to fetch the user details.');
      }
    }
  }

  /**
   * Get authenticated user data
   */
  private async fetchCurrentUser(): Promise<void> {
    // Do not make another call if logged in user us already fecthed
    if (this.currentUser) {
      return;
    }
    this.retrieveStarted();
    try {
      const response = await firstValueFrom(this.api.get$('/me.json', true));
      this.retrieveStopped();
      this.currentUser = response['data'];
    } catch (error) {
      console.error('[AccountService] Failed to load current user', error);
      this.retrieveStopped();
      return Promise.reject(error);
    }
  }

  /**
   * Get the target user in case of login as when the app refreshes or is re-opened.
   * The API call is only made when login as flow was used of the login i.e. targetUserUUID is present.
   */
  private async fetchTargetUser(): Promise<void> {
    // Do not make another call if target user is already present
    if (this.targetUser) {
      return;
    }
    try {
      const targetUserUUID = await this.authTokenService.getTargetUUID();
      // fetch the target user details only when target user uuid is present.
      if (targetUserUUID) {
        const tokenResponse = await firstValueFrom(this.api.post$('/api/v1/user/token', {}, true));
        this.targetUser = tokenResponse['target_user_details'];
      }
    } catch (error) {
      console.error('[AccountService] Failed to load target user', error);
      return Promise.reject(error);
    }
  }

  /**
   * Get users account data
   *
   * @param remoteOnly - If the call should required latest data.
   */
  async account(remoteOnly = false): Promise<User> {
    this.retrieveStarted();
    if (!this.user || remoteOnly) {
      try {
        const res = await this.api.gqlQuery(gql`
          query {
            user {
              hasActiveSoftBan
              address
              avatar {
                small
                big
              }
              contactPhone
              consentToCollectSensitiveInformation
              email
              firstName
              fullName
              id
              uid
              uuid
              lastName
              isHcpFunded
              organisation {
                businessName
                id
              }
              permalink
              truncatedName
              displayName
              userHasBlocked
              userIsBlocked
              normalizedType
              profile {
                ... on WorkerProfile {
                  currentWorkingWithChildrenCheck
                  workingWithChildren
                  emergencyContactsName
                  emergencyContactsPhone
                  emergencyContactsRelationship
                  emergencyContactsLastName
                  postalAddressLine1
                  postalAddressLine2
                  isMigratedToAvailabilityUplift
                }
                ... on ClientProfile {
                  estimatedWeeklySupportHour
                }
              }
              clientTypeName
              accountType
              addressLine1
              addressLine2
              approved
              askForFluShot
              birthDate
              carersInArea
              covidVaccineUpdatedAt
              flexEnabledAt
              gender
              hasActiveAgreement
              hasApprovedTimesheet
              hasCovidVaccine
              hasFairworkPendingNotification
              hasFirstAgreement
              horizonOneOffer {
                id
                optedOutReasons
                status
                suspensionStatus
                suspensionReason
                eligibility
                showModal
                suspendedClients {
                  fullName
                }
                testGroup
              }
              isOnboardingComplete
              jobsInArea
              learningHubAccessUrl
              postcode
              profileSteps {
                isComplete
                name
                value
              }
              receiveAgreementsEmails
              receiveEventDeliveriesEmails
              receiveMessagesEmails
              receivePlannedSessionReminderEmails
              receiveSms
              roles
              state
              participants {
                age
                firstName
                gender
                id
                lastName
                fullName
              }
              previousSearch
              hasPendingTimesheetsWithAutoApprovalOff
              canCreateLmj
              acceptedTaxAndSuperSyncTermsAndConditions
              timezone
              contactPhoneVerified
              hasMaxedSmsVerificationRequests
              coordinator {
                truncatedName
              }
            }
          }
        `);
        this.retrieveStopped();
        this.userSubj.next(res['user']);
        this.user = res['user'];
        return this.user;
      } catch (error) {
        this.retrieveStopped();
        return Promise.reject(error);
      }
    }
    return this.user;
  }

  /**
   * Determines if the account data is ready for use
   */
  async accountDataReady(): Promise<boolean> {
    if (this.user) {
      return Promise.resolve(true);
    }

    try {
      await this.account();
      return Promise.resolve(true);
    } catch (error) {
      return Promise.resolve(false);
    }
  }

  isAnyParticipantUnder18(): boolean {
    let isAnyParticipantUnder18 = false;
    this.user?.participants?.forEach((participant) => {
      // If any participant's age is null(not provided) or zero or greater than 150, we assume that they can be child
      if (!participant.age || participant.age < 18 || participant.age > 150) {
        isAnyParticipantUnder18 = true;
      }
    });
    return isAnyParticipantUnder18;
  }

  /**
   * Fetch sms verified from user account
   */
  async getPhoneVerificationStatus(): Promise<{ contactPhoneVerified: boolean }> {
    try {
      const res = await this.api.gqlQuery(gql`
        query {
          user {
            contactPhoneVerified
          }
        }
      `);
      return res['user'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request to delete user.
   *
   */
  async deleteUser(): Promise<any> {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation userDelete($UserDeleteInput: UserDeleteInput!) {
            userDelete(input: $UserDeleteInput) {
              user {
                id
              }
            }
          }
        `,
        { UserDeleteInput: {} },
      );
      return res;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Query to get user qualified for delete account.
   */
  checkCanDeleteAccount(): Observable<DeleteAccountCheck> {
    return this.api
      .gqlQuery$(gql`
        query {
          userDeletionStatus {
            canDelete
            reasons
          }
        }
      `)
      .pipe(
        map((res: DeleteAccountEligibilityRes) => res.userDeletionStatus),
        catchError(() => of({ canDelete: false })),
      );
  }

  /**
   * Get status if client has viewed the post new job prompt.
   *
   * @returns - return boolean status
   */
  getViewedPostNewJobPromptStatus(): Observable<boolean> {
    return this.api
      .gqlQuery$<{ user: User }>(gql`
        query {
          user {
            viewedPostNewJobPrompt
          }
        }
      `)
      .pipe(
        map((result) => result.user.viewedPostNewJobPrompt),
        // If catching error, then set it as true which is not showing the prompt up.
        catchError(() => of(true)),
      );
  }

  /**
   * Update viewed status if client has viewed the post new job prompt.
   *
   * @returns - return boolean status
   */
  updateViewedPostNewJobPromptStatus(): Observable<boolean> {
    return this.api
      .gqlMutation$<{
        clientUpdate: {
          user: {
            viewedPostNewJobPrompt: boolean;
          };
        };
      }>(gql`
        mutation {
          clientUpdate(input: { userAttributes: { viewedPostNewJobPrompt: true } }) {
            user {
              viewedPostNewJobPrompt
            }
          }
        }
      `)
      .pipe(
        map((result) => result.clientUpdate.user.viewedPostNewJobPrompt),
        catchError(() => of(false)),
      );
  }

  /**
   * Update consent status if client has given consent.
   *
   * @returns - return boolean status
   */
  updateConsentStatus(): Observable<boolean> {
    return this.api
      .gqlMutation$<{
        clientUpdate: {
          user: {
            consentToCollectSensitiveInformation: boolean;
          };
        };
      }>(gql`
        mutation {
          clientUpdate(input: { userAttributes: { consentToCollectSensitiveInformation: true } }) {
            user {
              consentToCollectSensitiveInformation
            }
          }
        }
      `)
      .pipe(
        map((result) => {
          const consent = result.clientUpdate.user.consentToCollectSensitiveInformation;
          consent && this.markConsentGiven();
          return consent;
        }),
        catchError(() => of(false)),
      );
  }

  /**
   * Manually updates the contact phone in the user settings to avoid recalling backend.
   *
   * @param contactPhone - The users new contact phone
   */
  manuallyUpdatePhoneNumber(contactPhone: string): void {
    this.user.contactPhone = contactPhone;
  }

  /**
   * Marks the users contact phone as verified manually so we avoid having to recall backend.
   */
  markContactPhoneVerified(): void {
    if (this.user) {
      this.user.smsVerified = true;
      this.user.contactPhoneVerified = true;
    }
  }

  /**
   * Marks the users given consent manually so we avoid having to recall backend.
   */
  markConsentGiven(): void {
    if (this.user) {
      this.user.consentToCollectSensitiveInformation = true;
    }
  }

  /**
   * Marks that the user has maxed their verify attempts so we avoid having to recall backend.
   */
  setHasMaxedSmsVerificationRequests(): void {
    if (this.user) {
      this.user.hasMaxedSmsVerificationRequests = true;
    }
  }

  /**
   * Set Target user details.
   *
   * @param targetUser - primary user details that needs to be stored and returned.
   */
  async setTargetUserAndUUID(targetUser?: AuthUser): Promise<void> {
    if (targetUser) {
      this.targetUser = targetUser;
      await this.storage.set(this.TARGET_UUID_KEY, targetUser.custom.user_uuid);
    } else {
      await this.resetTargetUserAndUUID();
    }
  }

  /**
   * Remove saved target user details.
   *
   */
  async resetTargetUserAndUUID(): Promise<void> {
    this.targetUser = null;
    await this.storage.remove(this.TARGET_UUID_KEY);
  }

  /**
   * Checks if the user is a given user type.
   *
   * @param userType - The type of user we are checking for.
   * @param primaryUser - True if you are after the "primary user".
   * @returns True if the current user matches the passed user type.
   */
  userIs(userType: string, primaryUser?: boolean): boolean {
    const accountType = this.getUser(primaryUser)?.custom?.account_type;
    return userType.toLowerCase() === accountType?.toLowerCase();
  }

  /**
   * Returns the user object.
   * `currentUser` is always primary user who has logged in
   * A non primary user can be `targetUser` if user is being impersonated or `currentUser`
   *
   * @param primaryUser - True if you are after the "primary user".
   */
  /* TODO (ES-33466): Remove any and make use of defined type of the modal */
  getUser(primaryUser?: boolean): any {
    if (primaryUser) {
      // primary user is used for login as button in the other places, so to keep it backward compatable, if target user is not present return null.
      return this.targetUser ? this.currentUser : null;
    }
    return this.targetUser || this.currentUser;
  }

  async initialiseAccountData(): Promise<void> {
    await this.account(true);
    await this.fetchCurrentUser();
  }

  resetAccountData(): void {
    // As the service data is not destroyed on logout, we need to reset the user account data on logout/session expiry.
    // This is to ensure that service has the logged in user data and not the previous one.
    this.user = null;
    this.userSubj.next(null);
    this.currentUser = null;
    this.targetUser = null;
  }

  /**
   * Returns the user's account type.
   *
   * @returns Current user's type.
   */
  getUserType(): string {
    return (this.getUser()?.custom?.account_type || '').toLowerCase();
  }
}
